import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import type { ExtractionReviewViewMode } from 'src/types/fieldExtraction';

export interface UserRole {
  userId: string;
  roleId: string;
  role: {
    id: string;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
    rolePermissions: {
      roleId: string;
      permissionId: string;
      permission: {
        id: string;
        name: string;
        resourceType: string;
        action: string;
      };
    }[];
  };
}

export interface UserContext {
  id: string;
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string;
  authProvider: string | null;
  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;
  profilePicPath: string;
  userRoles: UserRole[];
  profilePictureUrl: string;
  roles: string[];
  allowedTenants: {
    userId: string;
    tenantId: string;
    tenant: {
      id: string;
      name: string;
      configuration: {
        magicLinkAuth?: {
          enabled: boolean;
        };
        isSuperTenant?: boolean;
        extractionReviewViewMode?: ExtractionReviewViewMode;
      };
    };
  }[];
  externalEncompassId?: string;
}

/**
 * Simple version of getUser that doesn't require TanStack Query context
 * Used for direct calls from stores and utilities
 */
const getUserSimple = async (
  userId: string = 'myself',
): Promise<UserContext> => {
  if (isMockServices()) return await mock(_mockData);

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<{
    success: boolean;
    user: UserContext;
  }>(`/shared/users/${userId}`, {
    method: 'GET',
  });
  if (response.data) return response.data.user;
  throw new Error('Failed to fetch user');
};

export default getUserSimple;

const _mockData: UserContext = {
  id: 'cm8nm5bke0000o10i7d0y6zbh',
  tenantId: 'tenant_1',
  firstName: 'John',
  lastName: 'Doe',
  email: 'john.doe@example.com',
  authProvider: null,
  lastLogin: null,
  createdAt: '2025-06-18T16:53:09.092Z',
  updatedAt: '2025-06-20T15:05:45.187Z',
  profilePicPath:
    'tenants/tenant_1/users/cm8nm5bke0000o10i7d0y6zbh/profile-mock.png',
  userRoles: [
    {
      userId: 'cm8nm5bke0000o10i7d0y6zbh',
      roleId: 'role_1',
      role: {
        id: 'role_1',
        name: 'Tenant Admin',
        description: 'Super Admin • full access',
        createdAt: '2025-05-14T18:32:09.707Z',
        updatedAt: '2025-05-14T18:32:09.707Z',
        rolePermissions: [
          {
            roleId: 'role_1',
            permissionId: 'permission_1',
            permission: {
              id: 'permission_1',
              name: 'permission_1',
              resourceType: 'permission_1',
              action: 'permission_1',
            },
          },
        ],
      },
    },
  ],
  profilePictureUrl: 'https://via.placeholder.com/150',
  roles: ['Tenant Admin'],
  allowedTenants: [
    {
      userId: 'cm8nm5bke0000o10i7d0y6zbh',
      tenantId: 'tenant_1',
      tenant: {
        id: 'tenant_1',
        name: 'Tenant 1',
        configuration: {
          magicLinkAuth: {
            enabled: false,
          },
          isSuperTenant: false,
        },
      },
    },
  ],
  externalEncompassId: '1234567890',
};
